import { useState, useEffect } from "react";
import { Link, useLocation } from "wouter";
import { m, AnimatePresence } from "framer-motion";
import { cn } from "@/lib/utils";
import { whatsappHref, siteConfig } from "@/site.config";
import { track } from "@/lib/tracking";
import { IconWhatsApp, IconMenu, IconX } from "@/components/ui-sr/icons";
import { BazilioLogo } from "@/components/ui-sr/BazilioLogo";
import { VERTICAIS_LIST } from "@/data/verticais";

const navItems = [
  ...VERTICAIS_LIST.map(v => ({ href: v.routePath, label: v.navLabel })),
  { href: "/contato", label: "Contato" },
];

type SiteHeaderProps = {
  /** Quando true, header arranca em fundo branco (uso em páginas sem hero escuro). */
  forceSolid?: boolean;
};

export function SiteHeader({ forceSolid = false }: SiteHeaderProps) {
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [cardsStackActive, setCardsStackActive] = useState(false);
  const [location] = useLocation();

  useEffect(() => {
    const desktopQuery = window.matchMedia("(min-width: 1024px)");

    const updateHeaderState = () => {
      setScrolled(window.scrollY > 60);

      if (location !== "/" || !desktopQuery.matches || menuOpen) {
        setCardsStackActive(false);
        return;
      }

      const stack = document.querySelector<HTMLElement>("[data-verticais-stack]");
      if (!stack) {
        setCardsStackActive(false);
        return;
      }

      const rect = stack.getBoundingClientRect();
      const activationLine = 96;
      const active =
        rect.top <= activationLine && rect.bottom >= activationLine + 180;

      setCardsStackActive(active);
    };

    updateHeaderState();
    window.addEventListener("scroll", updateHeaderState, { passive: true });
    window.addEventListener("resize", updateHeaderState, { passive: true });

    return () => {
      window.removeEventListener("scroll", updateHeaderState);
      window.removeEventListener("resize", updateHeaderState);
    };
  }, [location, menuOpen]);

  const isSolid = scrolled || forceSolid;
  const isCardsGhosted = cardsStackActive && !menuOpen;
  const textInactive = isSolid
    ? "text-gray-600 hover:text-[var(--sr-navy)]"
    : "text-white/85 hover:text-white";
  const iconColor = isSolid ? "text-gray-800" : "text-white";

  return (
    <header
      className={cn(
        "fixed top-0 left-0 right-0 z-50 transition-[opacity,transform,background-color,backdrop-filter,box-shadow] duration-300",
        isCardsGhosted
          ? "opacity-[0.01] pointer-events-none"
          : "opacity-100 pointer-events-auto",
        isSolid ? "bg-white/95 backdrop-blur-md shadow-sm" : "bg-transparent"
      )}
    >
      <div className="w-full max-w-7xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
        <Link
          href="/"
          className="flex items-center gap-2 shrink-0 sr-focus-ring rounded"
          aria-label={`${siteConfig.brand.name}. página inicial`}
        >
          <BazilioLogo
            variant="full"
            tone={isSolid ? "light" : "dark"}
            className="h-9 sm:h-11 w-auto"
          />
        </Link>

        <nav
          className="hidden lg:flex items-center gap-6"
          aria-label="Navegação principal"
        >
          {navItems.map(item => {
            const active = location === item.href;
            return (
              <Link
                key={item.href}
                href={item.href}
                className={cn(
                  "text-[13px] transition-colors sr-focus-ring rounded",
                  active
                    ? cn(
                        "font-semibold underline underline-offset-4 decoration-2",
                        isSolid
                          ? "text-[var(--sr-gold-ink)]"
                          : "text-[var(--sr-gold-light)]"
                      )
                    : cn("font-medium", textInactive)
                )}
                aria-current={active ? "page" : undefined}
              >
                {item.label}
              </Link>
            );
          })}
        </nav>

        <a
          href={whatsappHref()}
          target="_blank"
          rel="noopener noreferrer"
          onClick={() => track("whatsapp_clicked", { source: "header" })}
          className="hidden lg:inline-flex items-center gap-2 bg-[var(--sr-green-cta)] text-white text-xs font-semibold px-4 py-2.5 rounded-[var(--radius-sm-token)] hover:brightness-110 active:scale-[0.97] transition-all sr-focus-ring"
        >
          <IconWhatsApp className="w-4 h-4" />
          <span>{siteConfig.brand.whatsapp.displayNumber}</span>
        </a>

        <button
          onClick={() => setMenuOpen(v => !v)}
          className="lg:hidden p-2 min-w-[44px] min-h-[44px] flex items-center justify-center sr-focus-ring rounded"
          aria-label={menuOpen ? "Fechar menu" : "Abrir menu"}
          aria-expanded={menuOpen}
          type="button"
        >
          {menuOpen ? (
            <IconX className={cn("w-6 h-6", iconColor)} />
          ) : (
            <IconMenu className={cn("w-6 h-6", iconColor)} />
          )}
        </button>
      </div>

      <AnimatePresence>
        {menuOpen && (
          <m.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.2 }}
            className="lg:hidden bg-white border-t border-gray-100 px-4 py-4 shadow-lg"
          >
            {navItems.map(item => (
              <Link
                key={item.href}
                href={item.href}
                onClick={() => setMenuOpen(false)}
                className="block py-3 text-sm font-medium text-gray-700 border-b border-gray-50 last:border-0 sr-focus-ring rounded"
              >
                {item.label}
              </Link>
            ))}
            <a
              href={whatsappHref()}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => {
                track("whatsapp_clicked", { source: "header_mobile" });
                setMenuOpen(false);
              }}
              className="mt-3 flex items-center justify-center gap-2 bg-[var(--sr-green-cta)] text-white text-sm font-semibold px-4 py-3 rounded-[var(--radius-sm-token)] w-full hover:brightness-110 sr-focus-ring"
            >
              <IconWhatsApp className="w-4 h-4" />
              Falar no WhatsApp
            </a>
          </m.div>
        )}
      </AnimatePresence>
    </header>
  );
}
